import React, { useState } from 'react';
import {
  Box, Container, Typography, Grid, Card, CardContent,
  Button, Paper, Chip, Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { formatPrice, PRODUCTS } from '../services/paymentService';

const zodiacSigns = [
  { name: 'Aries', symbol: '♈', color: '#FF6B6B' },
  { name: 'Taurus', symbol: '♉', color: '#4ECDC4' },
  { name: 'Gemini', symbol: '♊', color: '#FFE66D' },
  { name: 'Cancer', symbol: '♋', color: '#A8E6CF' },
  { name: 'Leo', symbol: '♌', color: '#FFD93D' },
  { name: 'Virgo', symbol: '♍', color: '#95E1D3' },
  { name: 'Libra', symbol: '♎', color: '#FFA8B5' },
  { name: 'Scorpio', symbol: '♏', color: '#B983FF' },
  { name: 'Sagittarius', symbol: '♐', color: '#FF8C42' },
  { name: 'Capricorn', symbol: '♑', color: '#6C5B7B' },
  { name: 'Aquarius', symbol: '♒', color: '#4ECDC4' },
  { name: 'Pisces', symbol: '♓', color: '#C8A2C8' }
];

const SignSelectionPage = () => {
  const navigate = useNavigate();
  const [selectedSigns, setSelectedSigns] = useState([]);

  // Get the number of signs to select from sessionStorage (default to 3 for Big Three Bundle)
  const maxSigns = parseInt(sessionStorage.getItem('signSelectionCount') || '3', 10);
  const isSingleSign = maxSigns === 1;

  const handleSignToggle = (signName) => {
    if (selectedSigns.includes(signName)) {
      // Remove sign if already selected
      setSelectedSigns(selectedSigns.filter(s => s !== signName));
    } else if (selectedSigns.length < maxSigns) {
      // Add sign if under limit
      setSelectedSigns([...selectedSigns, signName]);
    }
  };

  const handleContinue = () => {
    if (selectedSigns.length === maxSigns) {
      // Store selected signs in sessionStorage
      if (isSingleSign) {
        // For single sign, store as selectedRisingSign
        sessionStorage.setItem('selectedRisingSign', selectedSigns[0]);
      } else {
        // For multiple signs, store as array
        sessionStorage.setItem('selectedSigns', JSON.stringify(selectedSigns));
      }
      // Navigate to checkout
      navigate('/checkout');
    }
  };

  return (
    <Box sx={{ py: 8, bgcolor: '#0a0a0f', minHeight: '100vh' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Typography variant="h2" align="center" gutterBottom sx={{ fontWeight: 700 }}>
            {isSingleSign ? 'Choose Your Sign' : `Choose Your ${maxSigns} Signs`}
          </Typography>
          <Typography variant="h6" align="center" color="text.secondary" paragraph>
            {isSingleSign
              ? 'Select your Rising Sign for your extended forecast'
              : `Select any ${maxSigns} zodiac signs for your Big Three Bundle`
            }
          </Typography>
          {!isSingleSign && (
            <Typography variant="body1" align="center" color="text.secondary" paragraph>
              Tip: Most people choose their Rising, Sun, and Moon signs
            </Typography>
          )}

          {/* Selection Counter */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4, gap: 2 }}>
            {Array.from({ length: maxSigns }, (_, i) => i + 1).map((num) => (
              <Chip
                key={num}
                label={isSingleSign ? 'Your Sign' : `Sign ${num}`}
                color={selectedSigns[num - 1] ? 'primary' : 'default'}
                sx={{
                  fontSize: '1rem',
                  py: 2.5,
                  px: 1,
                  fontWeight: 600
                }}
                icon={selectedSigns[num - 1] ? <CheckCircleIcon /> : undefined}
              />
            ))}
          </Box>

          {/* Info Alert */}
          {selectedSigns.length < maxSigns && (
            <Alert severity="info" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
              {isSingleSign
                ? 'Please select your sign to continue'
                : `Please select ${maxSigns - selectedSigns.length} more sign${maxSigns - selectedSigns.length > 1 ? 's' : ''} to continue`
              }
            </Alert>
          )}

          {selectedSigns.length === maxSigns && (
            <Alert severity="success" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
              {isSingleSign
                ? 'Perfect! Click Continue to proceed to checkout.'
                : `Perfect! You've selected your ${maxSigns} signs. Click Continue to proceed to checkout.`
              }
            </Alert>
          )}
        </motion.div>

        {/* Zodiac Grid */}
        <Grid container spacing={3}>
          {zodiacSigns.map((sign, index) => {
            const isSelected = selectedSigns.includes(sign.name);
            const selectionOrder = selectedSigns.indexOf(sign.name) + 1;

            return (
              <Grid item xs={6} sm={4} md={3} key={sign.name}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: isSelected ? '3px solid' : '2px solid transparent',
                      borderColor: isSelected ? sign.color : 'transparent',
                      bgcolor: isSelected ? `${sign.color}11` : 'white',
                      transition: 'all 0.3s',
                      position: 'relative',
                      '&:hover': {
                        boxShadow: 6,
                        borderColor: sign.color,
                      }
                    }}
                    onClick={() => handleSignToggle(sign.name)}
                  >
                    {isSelected && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: sign.color,
                          color: 'white',
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          fontSize: '1rem'
                        }}
                      >
                        {selectionOrder}
                      </Box>
                    )}
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                      <Typography variant="h2" sx={{ color: sign.color, mb: 1 }}>
                        {sign.symbol}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {sign.name}
                      </Typography>
                      {isSelected && (
                        <CheckCircleIcon sx={{ color: sign.color, mt: 1, fontSize: 28 }} />
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            );
          })}
        </Grid>

        {/* Selected Signs Summary */}
        {selectedSigns.length > 0 && (
          <Paper sx={{ mt: 6, p: 4, maxWidth: 600, mx: 'auto' }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              {isSingleSign ? 'Your Selected Sign:' : 'Your Selected Signs:'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              {selectedSigns.map((signName, idx) => {
                const sign = zodiacSigns.find(s => s.name === signName);
                return (
                  <Chip
                    key={signName}
                    label={`${idx + 1}. ${sign.symbol} ${signName}`}
                    sx={{
                      bgcolor: sign.color,
                      color: 'white',
                      fontSize: '1rem',
                      py: 2.5,
                      px: 1,
                      fontWeight: 600
                    }}
                    onDelete={() => handleSignToggle(signName)}
                  />
                );
              })}
            </Box>

            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleContinue}
              disabled={selectedSigns.length !== maxSigns}
              sx={{
                py: 2,
                fontSize: '1.1rem',
                background: selectedSigns.length === maxSigns
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  : 'grey.300',
                '&:hover': {
                  background: selectedSigns.length === maxSigns
                    ? 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                    : 'grey.300',
                },
                '&:disabled': {
                  color: 'grey.500'
                }
              }}
            >
              Continue to Checkout
            </Button>

            <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 2 }}>
              {isSingleSign
                ? `Total: ${formatPrice(PRODUCTS.SINGLE_RISING.amount_cents, PRODUCTS.SINGLE_RISING.currency)} for 1 complete forecast`
                : `Total: ${formatPrice(PRODUCTS.BIG_THREE_BUNDLE.amount_cents, PRODUCTS.BIG_THREE_BUNDLE.currency)} for ${maxSigns} complete forecasts`
              }
            </Typography>
          </Paper>
        )}

        {/* Back Button */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button
            variant="text"
            onClick={() => navigate('/pricing?section=forecasts')}
            sx={{ color: 'text.secondary' }}
          >
            ← Back to Pricing
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default SignSelectionPage;
